// How things are written: dates, clocks, results, moves.

import type { Score } from "../board/lines.ts";

export const cap = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

export const fmtDate = (unix?: number | null): string =>
  unix ? new Date(unix * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";

export const fmtTime = (unix?: number | null): string =>
  unix ? new Date(unix * 1000).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";

export const fmtCount = (n?: number | null): string => Number(n || 0).toLocaleString("en-US");

/** 0.41 → "41%", 0.003 → "0.3%": small shares keep a decimal so they are not 0%. */
export function pct(x?: number | null): string {
  if (x == null || Number.isNaN(x)) return "—";
  const p = x * 100;
  if (p > 0 && p < 1) return p.toFixed(1).replace(/\.0$/, "") + "%";
  return Math.round(p) + "%";
}

/** "120+1" → "2+1", "30+0" → "½+0": the clock as Lichess writes it. */
export function clockLabel(tc?: string | null): string {
  const m = String(tc || "").match(/^(\d+)\+(\d+)$/);
  if (!m) return tc || "";
  const seconds = Number(m[1]);
  const fractions: Record<number, string> = { 15: "¼", 30: "½", 45: "¾" };
  const base = seconds % 60 === 0 ? String(seconds / 60) : fractions[seconds] || (seconds / 60).toFixed(1).replace(/\.0$/, "");
  return `${base}+${m[2]}`;
}

/** Enough of a game to say which it was. */
export interface GameInfo {
  game_id?: string | null;
  color: "white" | "black";
  white?: string;
  black?: string;
  result?: string;
  speed?: string;
  time_control?: string;
  played_at?: number | null;
  event?: string;
}

export const speedLabel = (g: GameInfo): string => [g.speed ? cap(g.speed) : "", clockLabel(g.time_control)].filter(Boolean).join(" ");

/** "won", "lost" or "drew", from the player's side. */
export function outcomeOf(g: GameInfo): "won" | "lost" | "drew" | "" {
  if (g.result === "1-0") return g.color === "white" ? "won" : "lost";
  if (g.result === "0-1") return g.color === "black" ? "won" : "lost";
  if (g.result === "1/2-1/2") return "drew";
  return "";
}

export const opponentOf = (g: GameInfo): string => (g.color === "white" ? g.black : g.white) || "?";

/** "19." for White's move, "19…" for Black's. */
export const moveLabel = (moveNumber: number, color: string): string => `${moveNumber}${color === "black" ? "…" : "."}`;

export const moveNumberOf = (ply: number): number => Math.floor(ply / 2) + 1;

/** "6." before White's move at ply 10, "6…" before Black's at ply 11. */
export const plyLabel = (ply: number): string => `${moveNumberOf(ply)}${ply % 2 ? "…" : "."}`;

/** A FEN without its move counters: how the server names a position. */
export const epdOf = (fen: string): string => String(fen || "").split(/\s+/).slice(0, 4).join(" ");

/** A score turned round into White's point of view. The analysis stores every
 *  score from the player's side; every number on screen is White's. */
export const whitePov = (score: Score, color: string): Score => (color === "black" ? { kind: score.kind, value: -score.value } : score);

/** "+1.40", "−0.35", "#3", "−#2": a report's numbers, from White's side. */
export const fmtScore = (score: Score): string =>
  score.kind === "mate"
    ? (score.value < 0 ? "−" : "") + "#" + Math.abs(score.value)
    : (score.value >= 0 ? "+" : "−") + (Math.abs(score.value) / 100).toFixed(2);

/** "Today", "Yesterday", "Mon, 3 Mar": which day a game was played. */
export function dayLabel(unix?: number | null): string {
  if (!unix) return "Undated";
  const d = new Date(unix * 1000);
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(today) - startOf(d)) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  const opts: Intl.DateTimeFormatOptions = { weekday: "short", day: "numeric", month: "short" };
  if (d.getFullYear() !== today.getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

/** "2.4 MB" — a size as a person reads it. */
export function fmtBytes(n?: number | null): string {
  const bytes = Number(n || 0);
  if (bytes < 1024) return `${bytes} B`;
  const units = ["kB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** "4 minutes ago", "3 days ago", "never". */
export function fmtAgo(unix?: number | null, now?: number): string {
  if (!unix) return "never";
  const seconds = Math.max(0, (now || Math.floor(Date.now() / 1000)) - unix);
  if (seconds < 90) return "just now";
  if (seconds >= 2592000) return fmtDate(unix);
  const steps: [number, string][] = [
    [60, "minute"],
    [3600, "hour"],
    [86400, "day"],
    [604800, "week"],
  ];
  let label = "minute";
  let size = 60;
  for (const [step, name] of steps) {
    if (seconds >= step) {
      size = step;
      label = name;
    }
  }
  const n = Math.round(seconds / size);
  return `${n} ${label}${n === 1 ? "" : "s"} ago`;
}

/** "6d 4h", "3h 20m", "12m" — how long something has been running. */
export function fmtSpan(seconds?: number | null): string {
  const s = Math.max(0, Math.floor(seconds || 0));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Lower-case and strip accents, for searching: a sixth of the opening
 *  names carry one, and nobody reaches for the umlaut key. */
export const fold = (s: string): string =>
  String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/** The levels the opening book counts games at. */
export const BANDS = [
  { value: "under1300", label: "Under 1300" },
  { value: "from1300", label: "1300–1700" },
  { value: "from1700", label: "1700–2100" },
  { value: "from2100", label: "2100+" },
];
export const bandLabel = (band: string): string => BANDS.find((b) => b.value === band)?.label || "";
